import React, { createContext, useContext, useCallback, useState } from "react";
import dayjs from "dayjs";

/**
 * Context for managing site approval data and functionality.
 * @type {React.Context}
 */
const SiteApprovalContext = createContext();

/**
 * Custom hook for accessing the site approval context.
 * @returns {object} Site approval context
 */
export const useSiteApprovalContext = () => {
    return useContext(SiteApprovalContext);
};

/**
 * Provider component for managing site approval context.
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} SiteApprovalProvider component JSX
 */
export const SiteApprovalProvider = ({ children }) => {

    // State and functions for managing site approval context   
    const [activeTab, setActiveTab] = useState("tab1");
    const [refreshCounter, setRefreshCounter] = useState(0);

    /**
     * Callback function to trigger data refresh.
     */
    const refreshData1 = useCallback(() => {
        // Increment the counter to trigger a refresh
        setRefreshCounter((prevCounter) => prevCounter + 1);
    }, []);

    /**
     * Function to handle tab selection.
     * @param {string} selectedTab - Selected tab key
     */
    const handleTabSelect = (selectedTab) => {
        setActiveTab(selectedTab);
        refreshData1();
    };


    const getFormattedDate = (date) => dayjs(date).format('YYYY-MM-DD');

    const getSevenDaysAgo = () => {
        return dayjs().subtract(7, 'day').toDate();
    };

    const today = new Date();
    const formattedDate = getFormattedDate(today);

    const sevenDaysAgo = getSevenDaysAgo();
    const formattedSevenDaysAgo = getFormattedDate(sevenDaysAgo);

    const [startDate, setStartDate] = useState(formattedSevenDaysAgo);
    const [endDate, setEndDate] = useState(formattedDate);

    /**
* Function to get the color based on status.
* @param {object} item - Data item containing status
* @returns {string} Color based on status
*/
    // Function to get the color based on status
    const getColorBasedOnStatus = (item) => {
        switch (item.status) {
            case 1: // Approved
                return 'green';
            case 3: // Pending
            case 6: // Another pending status
                return 'blue';
            case 2: // Rejected
                return 'red';
            default:
                return 'black'; // Default color or any other color you prefer
        }
    };

    /**
    * Function to get the color based on status.
    * @param {object} item - Data item containing status
    * @returns {string} Color based on status
    */
    // Function to get the color based on adCopies status
    const getColorBasedOnAdCopyStatus = (item) => {
        // Check if adCopies is an array and has elements
        if (Array.isArray(item.adCopies) && item.adCopies.length > 0) {
            // Iterate over adCopies array and get the status of the first adCopy
            const adCopyStatus = item.adCopies[0].status;

            switch (adCopyStatus) {
                case 1: // Approved
                    return 'green';
                case 3: // Pending
                case 6: // Another pending status
                    return 'blue';
                case 2: // Rejected
                    return 'red';
                default:
                    return 'black'; // Default color or any other color you prefer
            }
        }

        return 'black'; // Default color when adCopies is not an array or has no elements
    };

    // State for counts
    const [pendingCount, setPendingCount] = useState();
    const [approvedCount, setApprovedCount] = useState();
    const [rejectedCount, setRejectedCount] = useState();

    // Render the provider with its children
    // Provider value containing state and functions
    return (
        <SiteApprovalContext.Provider value={{
            getColorBasedOnAdCopyStatus, getColorBasedOnStatus, pendingCount, setPendingCount,
            approvedCount, setApprovedCount, rejectedCount, setRejectedCount,
            startDate, setStartDate, endDate, setEndDate, activeTab, setActiveTab,
            refreshCounter, setRefreshCounter, handleTabSelect, refreshData1
        }}>
            {children}
        </SiteApprovalContext.Provider>
    );
};
